/**
 * Curated Python completion data — builtins + common str/list/dict/set methods.
 *
 * No type inference: when the user types `x.`, we offer the union of common
 * methods across str/list/dict/set since we don't know `x`'s real type. Each
 * method's `detail` field prefixes the origin ("str", "list", etc.) so the
 * user can disambiguate.
 *
 * Keep the list short enough to stay readable. Algorithm-practice users don't
 * need all of stdlib — they need the ~80 things they actually type.
 *
 * Data shape is Monaco-agnostic here (plain objects). `python.ts` maps these
 * onto Monaco's CompletionItem shape at registration time so this file has
 * no runtime dependency on monaco-editor.
 */

export type Origin = 'builtin' | 'str' | 'list' | 'dict' | 'set' | 'constant';

export interface PyCompletion {
  /** What the user types to match. */
  label: string;
  /** Category: 'builtin' and 'constant' appear in the global completion list;
   *  the rest appear only after a `.` access. */
  origin: Origin;
  /** Signature-like string, shown inline in the completion menu. */
  detail: string;
  /** One-line description shown as hover doc. */
  documentation: string;
  /** Snippet-style text to insert. Use $0 for cursor position. */
  insertText: string;
}

/**
 * Global builtins + constants. These appear in the completion list on any
 * alpha-character trigger in a Python file.
 */
export const PY_GLOBALS: PyCompletion[] = [
  // Constants
  {
    label: 'True',
    origin: 'constant',
    detail: 'True: bool',
    documentation: 'Boolean true value.',
    insertText: 'True',
  },
  {
    label: 'False',
    origin: 'constant',
    detail: 'False: bool',
    documentation: 'Boolean false value.',
    insertText: 'False',
  },
  {
    label: 'None',
    origin: 'constant',
    detail: 'None',
    documentation: 'The null value in Python.',
    insertText: 'None',
  },

  // Type constructors (also used as predicates after isinstance)
  {
    label: 'bool',
    origin: 'builtin',
    detail: 'bool(x=False) -> bool',
    documentation: 'Return True when x is truthy, otherwise False.',
    insertText: 'bool($0)',
  },
  {
    label: 'int',
    origin: 'builtin',
    detail: 'int(x=0, base=10) -> int',
    documentation: 'Convert a number or string to an integer.',
    insertText: 'int($0)',
  },
  {
    label: 'float',
    origin: 'builtin',
    detail: 'float(x=0.0) -> float',
    documentation: 'Convert a number or string to a float.',
    insertText: 'float($0)',
  },
  {
    label: 'str',
    origin: 'builtin',
    detail: 'str(x="") -> str',
    documentation: 'Return the string representation of an object.',
    insertText: 'str($0)',
  },
  {
    label: 'list',
    origin: 'builtin',
    detail: 'list(iterable=()) -> list',
    documentation: 'Build a list from an iterable, or return a new empty list.',
    insertText: 'list($0)',
  },
  {
    label: 'dict',
    origin: 'builtin',
    detail: 'dict(**kwargs) -> dict',
    documentation: 'Create a new dictionary.',
    insertText: 'dict($0)',
  },
  {
    label: 'set',
    origin: 'builtin',
    detail: 'set(iterable=()) -> set',
    documentation: 'Build a set from an iterable, or return a new empty set.',
    insertText: 'set($0)',
  },
  {
    label: 'tuple',
    origin: 'builtin',
    detail: 'tuple(iterable=()) -> tuple',
    documentation: 'Build a tuple from an iterable.',
    insertText: 'tuple($0)',
  },
  {
    label: 'frozenset',
    origin: 'builtin',
    detail: 'frozenset(iterable=()) -> frozenset',
    documentation: 'Build an immutable set.',
    insertText: 'frozenset($0)',
  },
  {
    label: 'bytes',
    origin: 'builtin',
    detail: 'bytes(source) -> bytes',
    documentation: 'Build a bytes object.',
    insertText: 'bytes($0)',
  },

  // Introspection
  {
    label: 'type',
    origin: 'builtin',
    detail: 'type(object) -> type',
    documentation: 'Return the type of an object.',
    insertText: 'type($0)',
  },
  {
    label: 'isinstance',
    origin: 'builtin',
    detail: 'isinstance(obj, classinfo) -> bool',
    documentation: 'True if obj is an instance of the given class or tuple of classes.',
    insertText: 'isinstance($0)',
  },
  {
    label: 'issubclass',
    origin: 'builtin',
    detail: 'issubclass(cls, classinfo) -> bool',
    documentation: 'True if cls is a subclass of classinfo.',
    insertText: 'issubclass($0)',
  },
  {
    label: 'id',
    origin: 'builtin',
    detail: 'id(object) -> int',
    documentation: 'Return the unique identity of an object.',
    insertText: 'id($0)',
  },
  {
    label: 'hash',
    origin: 'builtin',
    detail: 'hash(object) -> int',
    documentation: 'Return the hash value of an object.',
    insertText: 'hash($0)',
  },
  {
    label: 'repr',
    origin: 'builtin',
    detail: 'repr(object) -> str',
    documentation: 'Return the canonical string representation of an object.',
    insertText: 'repr($0)',
  },

  // Numeric
  {
    label: 'abs',
    origin: 'builtin',
    detail: 'abs(x) -> number',
    documentation: 'Return the absolute value of a number.',
    insertText: 'abs($0)',
  },
  {
    label: 'max',
    origin: 'builtin',
    detail: 'max(iterable, *, key=None) -> value',
    documentation: 'Return the largest item.',
    insertText: 'max($0)',
  },
  {
    label: 'min',
    origin: 'builtin',
    detail: 'min(iterable, *, key=None) -> value',
    documentation: 'Return the smallest item.',
    insertText: 'min($0)',
  },
  {
    label: 'sum',
    origin: 'builtin',
    detail: 'sum(iterable, start=0) -> value',
    documentation: 'Sum of an iterable plus start.',
    insertText: 'sum($0)',
  },
  {
    label: 'pow',
    origin: 'builtin',
    detail: 'pow(base, exp, mod=None) -> value',
    documentation: 'Return base raised to exp; with mod, return (base**exp) % mod.',
    insertText: 'pow($0)',
  },
  {
    label: 'round',
    origin: 'builtin',
    detail: 'round(number, ndigits=None) -> value',
    documentation: 'Round a number to a given precision.',
    insertText: 'round($0)',
  },
  {
    label: 'divmod',
    origin: 'builtin',
    detail: 'divmod(a, b) -> (quotient, remainder)',
    documentation: 'Return the pair (a // b, a % b).',
    insertText: 'divmod($0)',
  },

  // Iterable / functional
  {
    label: 'len',
    origin: 'builtin',
    detail: 'len(obj) -> int',
    documentation: 'Return the length of a sequence, mapping, or set.',
    insertText: 'len($0)',
  },
  {
    label: 'range',
    origin: 'builtin',
    detail: 'range(stop) | range(start, stop, step=1)',
    documentation: 'Immutable sequence of numbers.',
    insertText: 'range($0)',
  },
  {
    label: 'enumerate',
    origin: 'builtin',
    detail: 'enumerate(iterable, start=0) -> iterator',
    documentation: 'Yield (index, item) pairs.',
    insertText: 'enumerate($0)',
  },
  {
    label: 'zip',
    origin: 'builtin',
    detail: 'zip(*iterables, strict=False) -> iterator',
    documentation: 'Yield tuples of items paired across iterables.',
    insertText: 'zip($0)',
  },
  {
    label: 'map',
    origin: 'builtin',
    detail: 'map(func, *iterables) -> iterator',
    documentation: 'Apply func to each item across iterables.',
    insertText: 'map($0)',
  },
  {
    label: 'filter',
    origin: 'builtin',
    detail: 'filter(func, iterable) -> iterator',
    documentation: 'Yield items for which func(item) is truthy.',
    insertText: 'filter($0)',
  },
  {
    label: 'sorted',
    origin: 'builtin',
    detail: 'sorted(iterable, *, key=None, reverse=False) -> list',
    documentation: 'Return a new sorted list.',
    insertText: 'sorted($0)',
  },
  {
    label: 'reversed',
    origin: 'builtin',
    detail: 'reversed(sequence) -> iterator',
    documentation: 'Return a reverse iterator over a sequence.',
    insertText: 'reversed($0)',
  },
  {
    label: 'all',
    origin: 'builtin',
    detail: 'all(iterable) -> bool',
    documentation: 'True if every element is truthy (or iterable is empty).',
    insertText: 'all($0)',
  },
  {
    label: 'any',
    origin: 'builtin',
    detail: 'any(iterable) -> bool',
    documentation: 'True if any element is truthy.',
    insertText: 'any($0)',
  },
  {
    label: 'iter',
    origin: 'builtin',
    detail: 'iter(obj) -> iterator',
    documentation: 'Return an iterator from a sequence or callable.',
    insertText: 'iter($0)',
  },
  {
    label: 'next',
    origin: 'builtin',
    detail: 'next(iterator, default=...) -> value',
    documentation: 'Advance an iterator; return default if exhausted.',
    insertText: 'next($0)',
  },

  // Character / base conversion
  {
    label: 'chr',
    origin: 'builtin',
    detail: 'chr(i) -> str',
    documentation: 'Return the character for Unicode code point i.',
    insertText: 'chr($0)',
  },
  {
    label: 'ord',
    origin: 'builtin',
    detail: 'ord(c) -> int',
    documentation: 'Return the Unicode code point of a single character.',
    insertText: 'ord($0)',
  },
  {
    label: 'bin',
    origin: 'builtin',
    detail: 'bin(x) -> str',
    documentation: 'Convert integer to binary string prefixed with "0b".',
    insertText: 'bin($0)',
  },
  {
    label: 'hex',
    origin: 'builtin',
    detail: 'hex(x) -> str',
    documentation: 'Convert integer to hex string prefixed with "0x".',
    insertText: 'hex($0)',
  },
  {
    label: 'oct',
    origin: 'builtin',
    detail: 'oct(x) -> str',
    documentation: 'Convert integer to octal string prefixed with "0o".',
    insertText: 'oct($0)',
  },
  {
    label: 'ascii',
    origin: 'builtin',
    detail: 'ascii(object) -> str',
    documentation: 'Return a string with non-ASCII chars escaped.',
    insertText: 'ascii($0)',
  },

  // Object attribute
  {
    label: 'getattr',
    origin: 'builtin',
    detail: 'getattr(object, name, default=...) -> value',
    documentation: 'Get a named attribute from an object.',
    insertText: 'getattr($0)',
  },
  {
    label: 'setattr',
    origin: 'builtin',
    detail: 'setattr(object, name, value) -> None',
    documentation: 'Set a named attribute on an object.',
    insertText: 'setattr($0)',
  },
  {
    label: 'hasattr',
    origin: 'builtin',
    detail: 'hasattr(object, name) -> bool',
    documentation: 'True if an object has the named attribute.',
    insertText: 'hasattr($0)',
  },
  {
    label: 'delattr',
    origin: 'builtin',
    detail: 'delattr(object, name) -> None',
    documentation: 'Delete a named attribute.',
    insertText: 'delattr($0)',
  },
  {
    label: 'callable',
    origin: 'builtin',
    detail: 'callable(object) -> bool',
    documentation: 'True if the object appears callable.',
    insertText: 'callable($0)',
  },

  // I/O
  {
    label: 'print',
    origin: 'builtin',
    detail: 'print(*values, sep=" ", end="\\n") -> None',
    documentation: 'Print values to stdout.',
    insertText: 'print($0)',
  },
  {
    label: 'input',
    origin: 'builtin',
    detail: 'input(prompt="") -> str',
    documentation: 'Read a line from stdin.',
    insertText: 'input($0)',
  },
  {
    label: 'open',
    origin: 'builtin',
    detail: 'open(file, mode="r", ...) -> file',
    documentation: 'Open a file and return a file object.',
    insertText: 'open($0)',
  },

  // Misc
  {
    label: 'format',
    origin: 'builtin',
    detail: 'format(value, format_spec="") -> str',
    documentation: 'Format a value using the given format spec.',
    insertText: 'format($0)',
  },
  {
    label: 'slice',
    origin: 'builtin',
    detail: 'slice(stop) | slice(start, stop, step=None)',
    documentation: 'Construct a slice object.',
    insertText: 'slice($0)',
  },
  {
    label: 'globals',
    origin: 'builtin',
    detail: 'globals() -> dict',
    documentation: 'Return the global namespace dict.',
    insertText: 'globals()',
  },
  {
    label: 'locals',
    origin: 'builtin',
    detail: 'locals() -> dict',
    documentation: 'Return the local namespace dict.',
    insertText: 'locals()',
  },
  {
    label: 'vars',
    origin: 'builtin',
    detail: 'vars(object=...) -> dict',
    documentation: "Return an object's __dict__.",
    insertText: 'vars($0)',
  },
];

/**
 * Method completions offered on `.` access. We don't know the receiver type,
 * so we return the union across str/list/dict/set. Each entry's `detail`
 * includes the origin so the user can tell them apart.
 */
export const PY_METHODS: PyCompletion[] = [
  // ---- str methods ----
  {
    label: 'upper',
    origin: 'str',
    detail: '(str) -> str',
    documentation: 'Return a copy with all characters uppercased.',
    insertText: 'upper()',
  },
  {
    label: 'lower',
    origin: 'str',
    detail: '(str) -> str',
    documentation: 'Return a copy with all characters lowercased.',
    insertText: 'lower()',
  },
  {
    label: 'title',
    origin: 'str',
    detail: '(str) -> str',
    documentation: 'Return a title-cased copy.',
    insertText: 'title()',
  },
  {
    label: 'capitalize',
    origin: 'str',
    detail: '(str) -> str',
    documentation: 'Return a copy with the first character capitalized.',
    insertText: 'capitalize()',
  },
  {
    label: 'strip',
    origin: 'str',
    detail: '(str, chars=None) -> str',
    documentation: 'Strip leading and trailing characters.',
    insertText: 'strip($0)',
  },
  {
    label: 'lstrip',
    origin: 'str',
    detail: '(str, chars=None) -> str',
    documentation: 'Strip leading characters.',
    insertText: 'lstrip($0)',
  },
  {
    label: 'rstrip',
    origin: 'str',
    detail: '(str, chars=None) -> str',
    documentation: 'Strip trailing characters.',
    insertText: 'rstrip($0)',
  },
  {
    label: 'split',
    origin: 'str',
    detail: '(str, sep=None, maxsplit=-1) -> list[str]',
    documentation: 'Split the string; default splits on whitespace.',
    insertText: 'split($0)',
  },
  {
    label: 'rsplit',
    origin: 'str',
    detail: '(str, sep=None, maxsplit=-1) -> list[str]',
    documentation: 'Split from the right.',
    insertText: 'rsplit($0)',
  },
  {
    label: 'splitlines',
    origin: 'str',
    detail: '(str, keepends=False) -> list[str]',
    documentation: 'Split on line boundaries.',
    insertText: 'splitlines($0)',
  },
  {
    label: 'join',
    origin: 'str',
    detail: '(str, iterable) -> str',
    documentation: 'Concatenate an iterable of strings with this string as separator.',
    insertText: 'join($0)',
  },
  {
    label: 'replace',
    origin: 'str',
    detail: '(str, old, new, count=-1) -> str',
    documentation: 'Return a copy with occurrences of old replaced by new.',
    insertText: 'replace($0)',
  },
  {
    label: 'startswith',
    origin: 'str',
    detail: '(str, prefix) -> bool',
    documentation: 'True if the string starts with the given prefix.',
    insertText: 'startswith($0)',
  },
  {
    label: 'endswith',
    origin: 'str',
    detail: '(str, suffix) -> bool',
    documentation: 'True if the string ends with the given suffix.',
    insertText: 'endswith($0)',
  },
  {
    label: 'find',
    origin: 'str',
    detail: '(str, sub, start=0, end=...) -> int',
    documentation: 'Return the lowest index of sub, or -1 if not found.',
    insertText: 'find($0)',
  },
  {
    label: 'rfind',
    origin: 'str',
    detail: '(str, sub, start=0, end=...) -> int',
    documentation: 'Return the highest index of sub, or -1 if not found.',
    insertText: 'rfind($0)',
  },
  {
    label: 'isdigit',
    origin: 'str',
    detail: '(str) -> bool',
    documentation: 'True if every character is a digit.',
    insertText: 'isdigit()',
  },
  {
    label: 'isalpha',
    origin: 'str',
    detail: '(str) -> bool',
    documentation: 'True if every character is alphabetic.',
    insertText: 'isalpha()',
  },
  {
    label: 'isalnum',
    origin: 'str',
    detail: '(str) -> bool',
    documentation: 'True if every character is alphanumeric.',
    insertText: 'isalnum()',
  },
  {
    label: 'isspace',
    origin: 'str',
    detail: '(str) -> bool',
    documentation: 'True if every character is whitespace.',
    insertText: 'isspace()',
  },
  {
    label: 'isupper',
    origin: 'str',
    detail: '(str) -> bool',
    documentation: 'True if every cased character is uppercase.',
    insertText: 'isupper()',
  },
  {
    label: 'islower',
    origin: 'str',
    detail: '(str) -> bool',
    documentation: 'True if every cased character is lowercase.',
    insertText: 'islower()',
  },
  {
    label: 'zfill',
    origin: 'str',
    detail: '(str, width) -> str',
    documentation: 'Pad the string on the left with zeros.',
    insertText: 'zfill($0)',
  },
  {
    label: 'encode',
    origin: 'str',
    detail: '(str, encoding="utf-8") -> bytes',
    documentation: 'Encode the string to bytes.',
    insertText: 'encode($0)',
  },

  // ---- list methods ----
  {
    label: 'append',
    origin: 'list',
    detail: '(list, value) -> None',
    documentation: 'Append an item to the end of the list.',
    insertText: 'append($0)',
  },
  {
    label: 'extend',
    origin: 'list',
    detail: '(list, iterable) -> None',
    documentation: 'Extend the list by appending items from an iterable.',
    insertText: 'extend($0)',
  },
  {
    label: 'insert',
    origin: 'list',
    detail: '(list, index, value) -> None',
    documentation: 'Insert a value before the given index.',
    insertText: 'insert($0)',
  },
  {
    label: 'remove',
    origin: 'list',
    detail: '(list, value) -> None',
    documentation: 'Remove the first occurrence of a value.',
    insertText: 'remove($0)',
  },
  {
    label: 'pop',
    origin: 'list',
    detail: '(list, index=-1) -> value',
    documentation: 'Remove and return the item at the given index.',
    insertText: 'pop($0)',
  },
  {
    label: 'sort',
    origin: 'list',
    detail: '(list, *, key=None, reverse=False) -> None',
    documentation: 'Sort the list in place.',
    insertText: 'sort($0)',
  },
  {
    label: 'reverse',
    origin: 'list',
    detail: '(list) -> None',
    documentation: 'Reverse the list in place.',
    insertText: 'reverse()',
  },
  {
    label: 'copy',
    origin: 'list',
    detail: '(list) -> list',
    documentation: 'Return a shallow copy of the list.',
    insertText: 'copy()',
  },
  {
    label: 'count',
    origin: 'list',
    detail: '(list, value) -> int',
    documentation: 'Count occurrences of a value.',
    insertText: 'count($0)',
  },
  {
    label: 'index',
    origin: 'list',
    detail: '(list, value, start=0, stop=...) -> int',
    documentation: 'Return the first index of a value.',
    insertText: 'index($0)',
  },
  {
    label: 'clear',
    origin: 'list',
    detail: '(list) -> None',
    documentation: 'Remove all items from the list.',
    insertText: 'clear()',
  },

  // ---- dict methods ----
  {
    label: 'keys',
    origin: 'dict',
    detail: '(dict) -> KeysView',
    documentation: 'Return a view of the dict\u2019s keys.',
    insertText: 'keys()',
  },
  {
    label: 'values',
    origin: 'dict',
    detail: '(dict) -> ValuesView',
    documentation: 'Return a view of the dict\u2019s values.',
    insertText: 'values()',
  },
  {
    label: 'items',
    origin: 'dict',
    detail: '(dict) -> ItemsView',
    documentation: 'Return a view of the dict\u2019s (key, value) pairs.',
    insertText: 'items()',
  },
  {
    label: 'get',
    origin: 'dict',
    detail: '(dict, key, default=None) -> value',
    documentation: 'Return the value for key; return default if absent.',
    insertText: 'get($0)',
  },
  {
    label: 'setdefault',
    origin: 'dict',
    detail: '(dict, key, default=None) -> value',
    documentation: 'Insert key with default if absent; return its value.',
    insertText: 'setdefault($0)',
  },
  {
    label: 'update',
    origin: 'dict',
    detail: '(dict, other) -> None',
    documentation: 'Update the dict with key/value pairs from other.',
    insertText: 'update($0)',
  },
  {
    label: 'fromkeys',
    origin: 'dict',
    detail: '(dict cls, iterable, value=None) -> dict',
    documentation: 'Create a new dict with keys from iterable and values set to value.',
    insertText: 'fromkeys($0)',
  },
  {
    label: 'popitem',
    origin: 'dict',
    detail: '(dict) -> (key, value)',
    documentation: 'Remove and return a (key, value) pair.',
    insertText: 'popitem()',
  },

  // ---- set methods ----
  {
    label: 'add',
    origin: 'set',
    detail: '(set, element) -> None',
    documentation: 'Add an element to the set.',
    insertText: 'add($0)',
  },
  {
    label: 'discard',
    origin: 'set',
    detail: '(set, element) -> None',
    documentation: 'Remove an element if present; no error if absent.',
    insertText: 'discard($0)',
  },
  {
    label: 'union',
    origin: 'set',
    detail: '(set, *others) -> set',
    documentation: 'Return a new set with elements from this and all others.',
    insertText: 'union($0)',
  },
  {
    label: 'intersection',
    origin: 'set',
    detail: '(set, *others) -> set',
    documentation: 'Return a new set of common elements.',
    insertText: 'intersection($0)',
  },
  {
    label: 'difference',
    origin: 'set',
    detail: '(set, *others) -> set',
    documentation: 'Return a new set of elements not in others.',
    insertText: 'difference($0)',
  },
  {
    label: 'symmetric_difference',
    origin: 'set',
    detail: '(set, other) -> set',
    documentation: 'Return a new set of elements in exactly one of the inputs.',
    insertText: 'symmetric_difference($0)',
  },
  {
    label: 'issubset',
    origin: 'set',
    detail: '(set, other) -> bool',
    documentation: 'True if every element in this set is in other.',
    insertText: 'issubset($0)',
  },
  {
    label: 'issuperset',
    origin: 'set',
    detail: '(set, other) -> bool',
    documentation: 'True if this set contains every element of other.',
    insertText: 'issuperset($0)',
  },
];
